import type { SettingsCatalogueEntry } from "@esi/shared";
import { t as translate } from "../i18n/i18n.ts";
import type { ProvenanceInfo } from "../settings-shell/ProvenanceMark.tsx";

/** Set at this level and in effect: an own value under a lock from above is not. */
export const isSetHere = (setting: SettingsCatalogueEntry) =>
  setting.ownValue && setting.lockedBy === null;

/**
 * Where a setting's value comes from, as the catalogue states it: set (overridden) at this
 * level, inherited from the organisation or an entity, locked above (by a level or the
 * deployment), and locking the levels below. A plain deployment default has no mark.
 */
export function provenanceOf(setting: SettingsCatalogueEntry): ProvenanceInfo[] {
  const marks: ProvenanceInfo[] = [];
  if (isSetHere(setting)) {
    marks.push({ kind: "overridden", label: translate("adminSettings.marks.overridden") });
  } else if (setting.source === "tenant") {
    marks.push({ kind: "inherited", label: translate("settings.behaviour.source.tenant") });
  } else if (setting.source === "entity") {
    marks.push({
      kind: "inherited",
      label: setting.sourceName
        ? translate("settings.behaviour.source.entity", { entity: setting.sourceName })
        : translate("settings.behaviour.source.entityUnknown"),
    });
  }
  if (setting.lockedBy) {
    marks.push({
      kind: "lockedAbove",
      label:
        setting.lockedBy.level === "instance"
          ? translate("settings.behaviour.locked.instance")
          : setting.lockedBy.level === "tenant"
            ? translate("settings.behaviour.locked.tenant")
            : translate("settings.behaviour.locked.entity", { entity: setting.lockedBy.name }),
    });
  } else if (setting.lockedHere) {
    marks.push({ kind: "locksBelow", label: translate("adminSettings.lockedHere") });
  }
  return marks;
}
